import { isNumber } from "lodash";

export type Size = number;
export type Amount = number;
export interface Bin {
  binSize: Size;
  sizeLeft: Size;
  sizeToQuantityMap: Map<Size, Amount>;
}

/**
 * Ein First Fit Decreasing - Algorithmus für das eindimmensionale Bin Packing bzw. Cutting Stock Problem, welches
 * NP-schwer ist. Der Algorithmus liefert schnell eine gute, aber nicht optimale Lösung. Die Laufzeit ist O(n^2).
 * Die Lösung ist garantiert nicht schlechter als 3/2 * die optimale Lösung.
 *
 * https://de.wikipedia.org/wiki/Eindimensionales_Zuschnittproblem
 * https://de.wikipedia.org/wiki/Beh%C3%A4lterproblem
 *
 * Beispiel: Bretter von 1.78 m Länge sollen in 3 Stücke zu 1 m, 3 Stücke zu 0.5 m und 7 Stücke zu 0.3 m geschnitten werden.
 *
 * const result = binPackingFirstFitDecreasing([1.78],new Map([[1,3],[0.5,3],[0.3,7]]));
 * // result = [
 *      {"sizeLeft":0.28,"parts":[[1,1],[0.5,1]]},
 *      {"sizeLeft":0.28,"parts":[[1,1],[0.5,1]]},
 *      {"sizeLeft":0.28,"parts":[[1,1],[0.5,1]]},
 *      {"sizeLeft":0.27999999999999986,"parts":[[0.3,5]]},
 *      {"sizeLeft":1.18,"parts":[[0.3,2]]}]
 *
 * Die Rechnung braucht etwa 10 ms, und das Ergebnis ist die Menge aller Aufteillungen jeweils eines Brettes.
 *
 * Das ersten drei Bretter werden jeweils in 1 Stück zu 1 m und 1 Stück zu 0.5 m aufgeteilt,
 * das folgende zu 5 Stücken zu 0.3 m und das letzte zu 2 Stück zu 0.3 m.
 */
export function binPackingFirstFitDecreasing(
  binSizes_: Size | Size[],
  sizeToQuantityInitialMap: Map<Size, Amount>,
): Bin[] {
  const binSizes = isNumber(binSizes_) ? [binSizes_] : binSizes_;
  if (binSizes.length == 0) {
    throw new Error("binSizes.length==0");
  }
  binSizes.forEach((binSize) => {
    if (binSize <= 0) {
      throw new Error(binSize + " must be larger than 0");
    }
  });

  sizeToQuantityInitialMap.forEach((quantity, size) => {
    if (!binSizes.some((binSize) => size <= binSize)) {
      throw new Error(size + " cannot fit into any bins: " + binSizes.join(", "));
    }
  });

  const entries = [...sizeToQuantityInitialMap.entries()].sort((a, b) => b[0] - a[0]);

  const bins: {
    binSize: number;
    sizeLeft: number;
    sizeToQuantityMap: Map<number, number>;
  }[] = [];

  entries.forEach(([size, quantity]) => {
    while (quantity > 0) {
      let bin = bins.find((bin) => bin.sizeLeft >= size);

      if (!bin) {
        // Find the smallest bin size that can accommodate the current size
        const suitableBinSize = binSizes.find((binSize) => binSize >= size);
        if (!suitableBinSize) {
          throw new Error(`No suitable bin found for size ${size}: ${binSizes.join(", ")}`);
        }

        bin = {
          binSize: suitableBinSize,
          sizeLeft: suitableBinSize,
          sizeToQuantityMap: new Map(),
        };
        bins.push(bin);
      }

      const quantityInBin = Math.min(quantity, Math.floor(bin.sizeLeft / size));
      quantity -= quantityInBin;
      bin.sizeLeft -= size * quantityInBin;
      bin.sizeToQuantityMap.set(size, (bin.sizeToQuantityMap.get(size) || 0) + quantityInBin);
    }
  });
  return bins;
}
